import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function PasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("The new password and confirmation don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated", { description: "Use your new password at the next sign-in." });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, "Could not update the password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-7" data-testid="password-card">
      <div className="flex items-center gap-3">
        <KeyRound className="h-5 w-5 text-gold" />
        <h2 className="font-display text-xl text-[#F3F4F6]">Administrator password</h2>
      </div>
      <p className="mt-2 text-sm text-[#9CA3AF]">
        Applies to your portal administrator sign-in only. Case-system accounts keep their own password.
      </p>

      <form onSubmit={submit} className="mt-6 grid gap-4 lg:grid-cols-3" data-testid="password-form">
        <div className="space-y-2">
          <Label htmlFor="current_password" className="text-xs uppercase tracking-widest text-[#9CA3AF]">
            Current password
          </Label>
          <Input
            id="current_password"
            data-testid="current-password-input"
            type="password"
            autoComplete="current-password"
            value={form.current_password}
            onChange={set("current_password")}
            className="bg-white/5 border-white/15"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new_password" className="text-xs uppercase tracking-widest text-[#9CA3AF]">
            New password
          </Label>
          <Input
            id="new_password"
            data-testid="new-password-input"
            type="password"
            autoComplete="new-password"
            value={form.new_password}
            onChange={set("new_password")}
            className="bg-white/5 border-white/15"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm_password" className="text-xs uppercase tracking-widest text-[#9CA3AF]">
            Confirm new password
          </Label>
          <Input
            id="confirm_password"
            data-testid="confirm-password-input"
            type="password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={set("confirm")}
            className="bg-white/5 border-white/15"
            required
          />
        </div>

        <p className="text-xs text-[#6B7280] lg:col-span-2">
          At least 10 characters and one symbol. You stay signed in on this device after the change.
        </p>
        <Button
          data-testid="change-password-btn"
          disabled={busy}
          className="rounded-full bg-gold text-navy hover:bg-gold-dark lg:justify-self-end"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>

        {error ? (
          <p
            data-testid="password-error"
            className="lg:col-span-3 rounded-md border-l-2 border-red-400 bg-red-400/10 px-3 py-2 text-sm text-red-300"
          >
            {error}
          </p>
        ) : null}
      </form>
    </div>
  );
}
